import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * ScrollToTop: drop-in scroll manager for React Router v6.
 *
 * Behaviour:
 *   1. On a real route change (pathname/search), scroll the window to (0, 0)
 *      so every navigation lands at the top of the destination page.
 *   2. If the URL has a hash (#section), defer one frame and scroll the
 *      matching element into view with the sticky header offset accounted
 *      for. We use scroll-margin-top via CSS (defined in index.css) so the
 *      section title is never hidden under the fixed nav.
 *   3. Skip scroll on POP navigation (browser Back/Forward) so the browser
 *      can restore the previous scroll position naturally.
 *
 * Designed to be safe in Samsung Internet, Chrome Android, iOS Safari, and
 * installed PWA standalone mode.
 */
export default function ScrollToTop() {
  const { pathname, search, hash, key } = useLocation();
  const prevKeyRef = useRef(key);

  useEffect(() => {
    // React Router updates `key` on every navigation; `history.action`
    // isn't exposed by the hook so we detect Back/Forward via popstate.
    const handler = () => {
      prevKeyRef.current = "__popstate__";
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  useEffect(() => {
    const isPop = prevKeyRef.current === "__popstate__";
    prevKeyRef.current = key;
    if (isPop) return; // let the browser restore the previous position

    if (hash) {
      // Defer one frame so the destination route has actually rendered.
      requestAnimationFrame(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
      });
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, search, hash, key]);

  return null;
}
